import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private readonly STORE_ID_KEY = 'restaurant_store_id';
  private readonly TIMEOUT_KEY = 'restaurant_timeout_minutes';
  private readonly VERSION_KEY = 'restaurant_app_version';
  private readonly CART_DISPLAY_MODE_KEY = 'restaurant_cart_display_mode';
  private readonly CAROUSEL_INTERVAL_KEY = 'restaurant_carousel_interval_seconds';
  private readonly LOGIN_MODE_KEY = 'restaurant_login_mode';
  private readonly DEFAULT_TIMEOUT_MINUTES = 5;
  private readonly DEFAULT_CAROUSEL_INTERVAL_SECONDS = 60;
  private readonly DEFAULT_LOGIN_MODE = 'optional';

  constructor() { }

  /**
   * 获取存储的轮播时间（秒）
   */
  getCarouselIntervalSeconds(): number {
    try {
      const intervalStr = localStorage.getItem(this.CAROUSEL_INTERVAL_KEY);
      if (intervalStr) {
        const interval = parseInt(intervalStr, 10);
        if (!isNaN(interval) && interval >= 1) { // 至少1秒
          return interval;
        }
      }
      return this.DEFAULT_CAROUSEL_INTERVAL_SECONDS;
    } catch (error) {
      console.error('获取轮播时间失败:', error);
      return this.DEFAULT_CAROUSEL_INTERVAL_SECONDS;
    }
  }

  /**
   * 保存轮播时间到本地存储
   */
  setCarouselIntervalSeconds(intervalSeconds: number): boolean {
    try {
      if (intervalSeconds >= 1) {
        localStorage.setItem(this.CAROUSEL_INTERVAL_KEY, intervalSeconds.toString());
      } else {
        localStorage.setItem(this.CAROUSEL_INTERVAL_KEY, this.DEFAULT_CAROUSEL_INTERVAL_SECONDS.toString());
      }
      return true;
    } catch (error) {
      console.error('保存轮播时间失败:', error);
      return false;
    }
  }

  /**
   * 获取购物车显示模式
   * @returns 'bottom' | 'floating' | 'top'
   */
  getCartDisplayMode(): 'bottom' | 'floating' | 'top' {
    try {
      const mode = localStorage.getItem(this.CART_DISPLAY_MODE_KEY);
      if (mode === 'top') return 'top';
      return (mode === 'floating') ? 'floating' : 'bottom';
    } catch (error) {
      console.error('获取购物车显示模式失败:', error);
      return 'bottom';
    }
  }

  /**
   * 设置购物车显示模式
   * @param mode 'bottom' | 'floating' | 'top'
   */
  setCartDisplayMode(mode: 'bottom' | 'floating' | 'top'): boolean {
    try {
      localStorage.setItem(this.CART_DISPLAY_MODE_KEY, mode);
      return true;
    } catch (error) {
      console.error('保存购物车显示模式失败:', error);
      return false;
    }
  }

  /**
   * 获取登录模式
   * @returns 'required' | 'optional'
   */
  getLoginMode(): 'required' | 'optional' {
    try {
      const mode = localStorage.getItem(this.LOGIN_MODE_KEY);
      if (mode === 'required') return 'required';
      return 'optional';
    } catch (error) {
      console.error('获取登录模式失败:', error);
      return 'optional';
    }
  }

  /**
   * 设置登录模式
   * @param mode 'required' | 'optional'
   */
  setLoginMode(mode: 'required' | 'optional'): boolean {
    try {
      localStorage.setItem(this.LOGIN_MODE_KEY, mode);
      return true;
    } catch (error) {
      console.error('保存登录模式失败:', error);
      return false;
    }
  }

  /**
   * 获取存储的storeId
   * @returns storeId字符串，如果不存在则返回空字符串
   */
  getStoreId(): string {
    try {
      return localStorage.getItem(this.STORE_ID_KEY) || '';
    } catch (error) {
      console.error('获取storeId失败:', error);
      return '';
    }
  }

  /** Read the configured numeric store ID without falling back to a demo store. */
  getNumericStoreId(): number | null {
    const configuredId = this.getStoreId().trim();
    if (!/^\d+$/.test(configuredId)) return null;
    const storeId = Number(configuredId);
    return Number.isSafeInteger(storeId) ? storeId : null;
  }

  /**
   * 保存storeId到本地存储
   * @param storeId 要保存的storeId
   * @returns 是否保存成功
   */
  setStoreId(storeId: string): boolean {
    try {
      if (storeId.trim()) {
        localStorage.setItem(this.STORE_ID_KEY, storeId.trim());
      } else {
        localStorage.removeItem(this.STORE_ID_KEY);
      }
      return true;
    } catch (error) {
      console.error('保存storeId失败:', error);
      return false;
    }
  }

  /**
   * 删除存储的storeId
   * @returns 是否删除成功
   */
  removeStoreId(): boolean {
    try {
      localStorage.removeItem(this.STORE_ID_KEY);
      return true;
    } catch (error) {
      console.error('删除storeId失败:', error);
      return false;
    }
  }

  /**
   * 检查是否已设置storeId
   * @returns 是否已设置storeId
   */
  hasStoreId(): boolean {
    return this.getStoreId().length > 0;
  }

  /**
   * 获取当前日期作为密码格式 (MMDDYYYY)
   * @returns 格式化的日期字符串
   */
  getCurrentDatePassword(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${month}${day}${year}`;
  }

  /**
   * 验证密码是否为当前日期
   * @param password 输入的密码
   * @returns 是否验证通过
   */
  validateDatePassword(password: string): boolean {
    const currentDatePassword = this.getCurrentDatePassword();
    return password.trim() === currentDatePassword;
  }

  /**
   * 获取存储的超时时间（分钟）
   * @returns 超时时间（分钟），如果不存在则返回默认值5分钟
   */
  getTimeoutMinutes(): number {
    try {
      const timeoutStr = localStorage.getItem(this.TIMEOUT_KEY);
      if (timeoutStr) {
        const timeout = parseInt(timeoutStr, 10);
        if (!isNaN(timeout) && timeout > 0 && timeout <= 60) {
          return timeout;
        }
      }
      return this.DEFAULT_TIMEOUT_MINUTES;
    } catch (error) {
      console.error('获取超时时间失败:', error);
      return this.DEFAULT_TIMEOUT_MINUTES;
    }
  }

  /**
   * 保存超时时间到本地存储
   * @param timeoutMinutes 要保存的超时时间（分钟），范围1-60
   * @returns 是否保存成功
   */
  setTimeoutMinutes(timeoutMinutes: number): boolean {
    try {
      if (timeoutMinutes > 0 && timeoutMinutes <= 60) {
        localStorage.setItem(this.TIMEOUT_KEY, timeoutMinutes.toString());
      } else {
        // 如果超出范围，使用默认值
        localStorage.setItem(this.TIMEOUT_KEY, this.DEFAULT_TIMEOUT_MINUTES.toString());
      }
      return true;
    } catch (error) {
      console.error('保存超时时间失败:', error);
      return false;
    }
  }

  /**
   * 删除存储的超时时间
   * @returns 是否删除成功
   */
  removeTimeoutMinutes(): boolean {
    try {
      localStorage.removeItem(this.TIMEOUT_KEY);
      return true;
    } catch (error) {
      console.error('删除超时时间失败:', error);
      return false;
    }
  }

  /**
   * 检查是否已设置自定义超时时间
   * @returns 是否已设置自定义超时时间
   */
  hasCustomTimeout(): boolean {
    return localStorage.getItem(this.TIMEOUT_KEY) !== null;
  }

  /**
   * 获取应用版本号
   */
  getAppVersion(): string {
    try {
      return localStorage.getItem(this.VERSION_KEY) || '';
    } catch (error) {
      console.error('获取应用版本失败:', error);
      return '';
    }
  }

  /**
   * 设置应用版本号
   */
  setAppVersion(version: string): boolean {
    try {
      const trimmed = (version || '').trim();
      if (trimmed) {
        localStorage.setItem(this.VERSION_KEY, trimmed);
      } else {
        localStorage.removeItem(this.VERSION_KEY);
      }
      return true;
    } catch (error) {
      console.error('保存应用版本失败:', error);
      return false;
    }
  }

  /**
   * 删除应用版本号
   */
  removeAppVersion(): boolean {
    try {
      localStorage.removeItem(this.VERSION_KEY);
      return true;
    } catch (error) {
      console.error('删除应用版本失败:', error);
      return false;
    }
  }
}
